import React from "react"

import { format } from "date-fns"
import { Box, Check, Package, Scale, X } from "lucide-react"

import { formatDuration } from "@/lib/routeDelayDisplay"
import { getStopTypeIcon, getStopTypeLabel } from "@/lib/slotTypeDisplay"
import {
  DELAY_THRESHOLD_MINUTES,
  getStopTimeVariance,
  stopVarianceBand,
} from "@/lib/statusDisplay"
import { type Tone, toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import { Stop } from "@/types/collections"

import { StopSourceBadge } from "../StopSourceBadge"

/**
 * Completed stops show a tick, others their position. Inferred stops get the same tick. Nothing
 * animates.
 */
const stopMarker = (status: string): { glyph: React.ReactNode | null; className: string } => {
  switch (status) {
    case "completed":
      return {
        glyph: <Check size={11} strokeWidth={3} />,
        className: "border-transparent bg-palette-jungleGreen500 text-white",
      }
    case "processing":
    case "arrived":
      return {
        glyph: null,
        className: "border-blue-500 bg-blue-50 text-blue-700 ring-2 ring-blue-500/20",
      }
    case "en_route":
      return { glyph: null, className: "border-blue-300 bg-white text-blue-600" }
    case "skipped":
      return {
        glyph: <X size={11} strokeWidth={3} />,
        className: "border-transparent bg-red-500 text-white",
      }
    default:
      return {
        glyph: null,
        className: "border-palette-jungleGreen300 bg-white text-palette-jungleGreen500",
      }
  }
}

interface RouteStopListProps {
  stops: Stop[]
  completedStops: number
  totalStops: number
  onOpenStop: (stopId: string) => void
}

const RouteStopList: React.FC<RouteStopListProps> = ({
  stops,
  completedStops,
  totalStops,
  onOpenStop,
}) => {
  /** -1 when the route is finished. */
  const nextStopIndex = stops.findIndex((s) => s.status !== "completed" && s.status !== "skipped")

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-sm font-semibold text-palette-jungleGreen700">Stops</h3>
        <span className="text-xs text-palette-jungleGreen500">
          {completedStops} of {totalStops} done
        </span>
      </div>

      {stops.length === 0 ? (
        <p className="py-6 text-center text-xs text-palette-jungleGreen400">
          This route has no stops.
        </p>
      ) : (
        <ol className="relative">
          {stops.map((stop, index) => {
            const marker = stopMarker(stop.status)
            const isLast = index === stops.length - 1
            const isNext = index === nextStopIndex
            const arrival = stop.actualArrival ?? stop.estimatedArrival ?? stop.plannedArrival
            const arrivalIsEstimate = !stop.actualArrival && !!stop.estimatedArrival
            /** Inferred and untimed: show an em dash, never the planned time. */
            const arrivalUnrecorded = stop.statusSource === "inferred" && !stop.actualArrival
            /**
             * delay_minutes is sync-populated and usually null, so measure from the timestamps
             * first.
             */
            const observedArrival = stop.actualArrival ?? stop.estimatedArrival
            const drift =
              getStopTimeVariance(stop.plannedArrival, observedArrival)?.minutes ??
              stop.delayMinutes ??
              0
            /** On-plan stays neutral (not `success`) so trouble stands out in a long column. */
            const band = stopVarianceBand(drift)
            const driftTone: Tone =
              band === "late"
                ? "danger"
                : band === "at_risk"
                  ? "warning"
                  : drift <= -DELAY_THRESHOLD_MINUTES
                    ? "success"
                    : "neutral"
            const isBehind = band !== "on_plan"
            const load = [
              (stop.volumeActual ?? stop.volumeEstimated ?? 0) > 0 && {
                icon: <Package size={10} />,
                text: `${stop.volumeActual ?? stop.volumeEstimated} items`,
              },
              (stop.weightActual ?? stop.weightEstimated ?? 0) > 0 && {
                icon: <Scale size={10} />,
                text: `${stop.weightActual ?? stop.weightEstimated} kg`,
              },
              (stop.loadingUnitsActual ?? stop.loadingUnitsEstimated ?? 0) > 0 && {
                icon: <Box size={10} />,
                text: `${stop.loadingUnitsActual ?? stop.loadingUnitsEstimated} LU`,
              },
            ].filter(Boolean) as { icon: React.ReactNode; text: string }[]

            return (
              <li key={stop.id} className="flex gap-3">
                <div className="flex w-5 flex-none flex-col items-center pt-1">
                  <span
                    className={cn(
                      "flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-semibold",
                      marker.className
                    )}
                  >
                    {marker.glyph ?? stop.sequence ?? index + 1}
                  </span>
                  {!isLast && (
                    <span
                      className={cn(
                        "my-1 w-px flex-1",
                        stop.status === "completed"
                          ? "bg-palette-jungleGreen300"
                          : "bg-palette-jungleGreen200"
                      )}
                    />
                  )}
                </div>

                <div className={cn("min-w-0 flex-1", isLast ? "pb-0" : "pb-4")}>
                  <div className="flex items-baseline gap-2">
                    {/* Only the name opens the stop, so a stray click on a time doesn't.
                        `min-w-0` lets the `truncate` work. */}
                    <button
                      type="button"
                      onClick={() => onOpenStop(stop.id)}
                      title={stop.name}
                      className="min-w-0 truncate rounded-sm text-left text-sm font-medium text-palette-jungleGreen900 underline-offset-2 hover:text-palette-jungleGreen700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-palette-jungleGreen400"
                    >
                      {stop.name}
                    </button>
                    <span
                      className={cn(
                        "ml-auto flex-none text-sm tabular-nums",
                        isBehind
                          ? cn("font-semibold", toneClasses(driftTone).text)
                          : "text-palette-jungleGreen900"
                      )}
                    >
                      {arrivalUnrecorded ? (
                        <span className="text-palette-jungleGreen400">—</span>
                      ) : (
                        format(arrival, "HH:mm")
                      )}
                      <StopSourceBadge stop={stop} className="ml-1" />
                    </span>
                  </div>

                  <div className="mt-0.5 flex items-baseline gap-2">
                    <span className="truncate text-[11px] text-palette-jungleGreen500">
                      <span aria-hidden="true">
                        {getStopTypeIcon(stop.slotType || stop.type)}
                      </span>{" "}
                      {getStopTypeLabel(stop.slotType || stop.type)} ·{" "}
                      {stop.postcode || stop.address}
                    </span>
                    <span className="ml-auto flex-none text-[11px] tabular-nums">
                      {arrivalIsEstimate && (
                        <span className="text-palette-jungleGreen400">ETA </span>
                      )}
                      {drift !== 0 ? (
                        <span className={cn("font-medium", toneClasses(driftTone).text)}>
                          {drift > 0 ? "+" : "−"}
                          {formatDuration(Math.abs(drift), true)}
                        </span>
                      ) : (
                        <span className="text-palette-jungleGreen400">
                          plan {format(stop.plannedArrival, "HH:mm")}
                        </span>
                      )}
                    </span>
                  </div>

                  {load.length > 0 && (
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-palette-jungleGreen500">
                      {load.map((item) => (
                        <span key={item.text} className="flex items-center gap-1">
                          {item.icon}
                          {item.text}
                        </span>
                      ))}
                    </div>
                  )}

                  {isNext && (
                    <span className="mt-1.5 inline-flex items-center rounded bg-palette-jungleGreen100 px-1.5 py-0.5 text-[10px] font-medium text-palette-jungleGreen700">
                      {stop.status === "processing" || stop.status === "arrived"
                        ? "At stop now"
                        : "Next stop"}
                    </span>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

export default RouteStopList
